import { useState, useEffect } from 'react';

/**
 * Proposal History Component
 * Displays a timeline of all edits made to a proposal
 */
export function ProposalHistory({ proposalId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Fetch proposal history on mount
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          action: 't24_get_proposal_history',
          nonce: window.t24Governance.nonce,
          proposal_id: proposalId,
        });

        const response = await fetch(
          `${window.t24Governance.ajaxUrl}?${params.toString()}`,
          {
            method: 'GET',
            credentials: 'same-origin',
          }
        );

        if (!response.ok) {
          throw new Error('Failed to load history');
        }

        const data = await response.json();

        if (data.success) {
          setHistory(data.data.history || []);
        } else {
          throw new Error(data.data?.message || 'Failed to load history');
        }
      } catch (err) {
        console.error('Failed to load proposal history:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (proposalId) {
      fetchHistory();
    }
  }, [proposalId]);

  // Toggle expanded state for a history item
  const toggleExpanded = (itemId) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Format timestamp
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get icon for field change
  const getFieldIcon = (fieldName) => {
    const icons = {
      title: '📝',
      description: '📄',
      category: '🏷️',
      status: '🔄',
      priority: '⚡',
      assigned_to: '👤',
      due_date: '📅',
      voting_deadline: '⏰',
    };
    return icons[fieldName] || '✏️';
  };

  // Render loading state
  if (loading) {
    return (
      <div className="t24-proposal-history-loading">
        <div className="t24-loading-spinner"></div>
        <p>Loading history...</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="t24-proposal-history-error">
        <p>Failed to load history: {error}</p>
      </div>
    );
  }

  // Render empty state
  if (!history || history.length === 0) {
    return (
      <div className="t24-proposal-history-empty">
        <p>No edit history available for this proposal.</p>
      </div>
    );
  }

  // Render history timeline
  return (
    <div className="t24-proposal-history">
      <h3 className="t24-proposal-history-title">Edit History</h3>

      <div className="t24-proposal-history-timeline">
        {history.map((item) => {
          const isExpanded = expandedItems.has(item.id);

          return (
            <div
              key={item.id}
              className={`t24-history-item ${isExpanded ? 'expanded' : ''}`}
            >
              {/* Timeline connector */}
              <div className="t24-history-connector">
                <div className="t24-history-dot"></div>
                {item.id !== history[history.length - 1].id && (
                  <div className="t24-history-line"></div>
                )}
              </div>

              {/* History content */}
              <div className="t24-history-content">
                {/* Header */}
                <div
                  className="t24-history-header"
                  onClick={() => toggleExpanded(item.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      toggleExpanded(item.id);
                    }
                  }}
                >
                  <span className="t24-history-field-icon">
                    {getFieldIcon(item.field_name)}
                  </span>
                  <div className="t24-history-summary">
                    <strong>{item.editor_name || 'Unknown'}</strong>{' '}
                    <span className="t24-history-change-summary">
                      {item.change_summary}
                    </span>
                  </div>
                  <button
                    className="t24-history-expand-btn"
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? '▼' : '▶'}
                  </button>
                </div>

                {/* Metadata */}
                <div className="t24-history-meta">
                  <span className="t24-history-time">
                    {formatDate(item.edited_at)}
                  </span>
                  {item.ip_address && item.ip_address !== '0.0.0.0' && (
                    <span className="t24-history-ip" title="IP Address">
                      🌐 {item.ip_address}
                    </span>
                  )}
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="t24-history-details">
                    <div className="t24-history-diff">
                      <div className="t24-history-diff-section old">
                        <div className="t24-history-diff-label">Previous:</div>
                        <div className="t24-history-diff-value">
                          {item.old_value || <em>(empty)</em>}
                        </div>
                      </div>
                      <div className="t24-history-diff-arrow">→</div>
                      <div className="t24-history-diff-section new">
                        <div className="t24-history-diff-label">New:</div>
                        <div className="t24-history-diff-value">
                          {item.new_value || <em>(empty)</em>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
